import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { ApiError } from "@/api/client";
import { HostPicker } from "@/components/HostPicker";
import { Button } from "@/components/ui/button";
import { startAnalysisDeferred } from "@/hooks/useAnalysisStream";
import { useSuggestedHosts } from "@/hooks/useSuggestedHosts";

export function ProactiveAnalysisPage() {
  const [target, setTarget] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const suggested = useSuggestedHosts();

  const submitFor = async (value: string) => {
    setError(null);
    const trimmed = value.trim();
    if (!trimmed) return;
    setSubmitting(true);
    try {
      // F14: defer_first_turn=true para navegar al chat de inmediato.
      // El primer turno arranca en vivo desde la página de la sesión.
      const session = await startAnalysisDeferred({
        trigger_type: "proactive",
        target_host: trimmed,
      });
      navigate(`/analysis/sessions/${session.id}`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 503) {
        setError(
          "El módulo de análisis profundo no está configurado en este servidor.",
        );
      } else if (err instanceof ApiError && err.status === 429) {
        setError("Cupo mensual de tokens agotado.");
      } else {
        setError(err instanceof Error ? err.message : "Error.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    submitFor(target);
  };

  const handlePickFromCard = (hostname: string) => {
    setTarget(hostname);
    submitFor(hostname);
  };

  const hosts = suggested.data?.hosts ?? [];
  const unavailable = suggested.data?.source === "unavailable" || suggested.isError;

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div>
        <h1 className="text-2xl font-semibold">Nuevo análisis proactivo</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          AROC diagnostica un host o subsistema sin incidente activo. Elige uno
          de los críticos preconfigurados o selecciónalo de la lista del
          Zabbix; si no aparece, escribe el nombre manualmente.
        </p>
      </div>
      <form
        onSubmit={handleSubmit}
        className="space-y-4 rounded-lg border bg-card p-4"
        aria-label="Iniciar análisis proactivo"
      >
        <HostPicker
          hosts={hosts}
          selectedHostname={target}
          onPickFromCard={handlePickFromCard}
          onPickFromCombobox={setTarget}
          onTypeFree={setTarget}
          disabled={submitting}
          isLoading={suggested.isLoading}
          unavailable={unavailable}
        />
        {error && (
          <div
            role="alert"
            className="rounded border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          >
            {error}
          </div>
        )}
        <Button type="submit" disabled={submitting || !target.trim()}>
          {submitting ? "Iniciando…" : "Iniciar análisis"}
        </Button>
      </form>
    </div>
  );
}
